import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { APP_URLS } from '../../../../urls';
import { fetchPatientData } from '../../../redux/middleware/user/patient/patient_data';
import { showToast } from '../../../utils/user/patient/helpers';
import {
  setPatientAuthenticated,
  setPatientCredentials
} from '../../../redux/actions/user/patient/auth/login';

/**
 * usePatientAuth
 * --------------
 * This hook initializes authentication by checking localStorage for credentials (if needed),
 * then retrieves the authentication state from Redux. Optionally, it can fetch the patient data
 * and redirect the user if they are not authenticated.
 *
 * @param {boolean} [fetchData=false] - If true, dispatches fetchPatientData() when credentials are available.
 * @param {boolean} [linkToUrl=false] - If true, redirects to the login page when not authenticated.
 * @param {Object} [toast=null] - Toast instance for showing notifications.
 * @returns {Object} An object containing isAuthenticated, credentials, and patientData.
 */
export function usePatientAuth (
  fetchData = false,
  linkToUrl = false,
  toast = null,
  requiresToken = false
) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Add a loading state to track initialization
  const [isInitializing, setIsInitializing] = useState(true);
  const [tokenCheckInterval, setTokenCheckInterval] = useState(null);

  // Initialization effect - securely load auth state
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Read directly from localStorage
        const storedAuth = localStorage.getItem('isAuthenticated');
        const storedCredentials = localStorage.getItem('patientCredentials');
        const tokenExpiry = localStorage.getItem('tokenExpiry');

        // Validate token expiration
        const isTokenValid = tokenExpiry && new Date(tokenExpiry) > new Date();

        if (storedAuth === 'true' && storedCredentials && isTokenValid) {
          // Update Redux state
          dispatch(setPatientAuthenticated(true));
          dispatch(setPatientCredentials(storedCredentials));

          // Optionally fetch patient data
          if (fetchData) {
            await dispatch(fetchPatientData());
          }
        } else if (storedAuth === 'true' && (!isTokenValid || !tokenExpiry)) {
          // Token expired or missing expiration - clear auth state
          clearAuthState(dispatch);
          if (toast) {
            showToast(
              toast,
              'warning',
              'Your session has expired. Please log in again.'
            );
          }
        }
      } finally {
        // Mark initialization as complete regardless of outcome
        setIsInitializing(false);
      }
    };

    initAuth();

    // Clear any existing interval
    if (tokenCheckInterval) {
      clearInterval(tokenCheckInterval);
    }

    // Set up periodic token validation (check every minute)
    const interval = setInterval(() => {
      const tokenExpiry = localStorage.getItem('tokenExpiry');
      if (tokenExpiry && new Date(tokenExpiry) <= new Date()) {
        clearAuthState(dispatch);
        if (toast) {
          showToast(
            toast,
            'warning',
            'Your session has expired. Please log in again.'
          );
        }
        if (linkToUrl) {
          navigate(APP_URLS.patientLogin);
        }
      }
    }, 60000);

    setTokenCheckInterval(interval);

    // Cleanup interval on unmount
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [dispatch, fetchData, navigate, toast, linkToUrl]);

  // Get the current values from Redux after initialization
  const {
    patient: patientAuthData,
    isAuthenticated,
    credentials
  } = useSelector((state) => state.patientAuth);
  const { patient: patientProfileData } = useSelector(
    (state) => state.patientData
  );

  // Only perform redirects after initialization is complete
  useEffect(() => {
    if (!isInitializing && !isAuthenticated && linkToUrl) {
      if (toast) {
        showToast(toast, 'info', 'Please log in to access this page');
      }
      navigate(APP_URLS.patientLogin);
    }
  }, [isInitializing, isAuthenticated, linkToUrl, navigate, toast]);

  return {
    isAuthenticated,
    credentials,
    patientData: { ...patientProfileData, ...patientAuthData },
    isLoading: isInitializing
  };
}

/**
 * Securely clear authentication state
 */
export const clearAuthState = (dispatch) => {
  try {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('patientCredentials');
    localStorage.removeItem('tokenExpiry');
  } catch (error) {
    console.error('Error clearing localStorage:', error);
  }

  // Then update Redux state
  dispatch(setPatientAuthenticated(false));
  dispatch(setPatientCredentials(null));
};
